// src/app/hooks/usePromptTrackingData.ts
// Fetches saved prompts from DB only — no LLM calls on load.
// Briefing insights are fetched separately by the AI section component
// when the user opens that tab or clicks Refresh.

import { useState, useEffect, useCallback } from 'react';
import { fetchPrompts } from '../../services/prompt-tracking/prompt-tracking-api';
import type {
  PromptFilters,
  PromptIntent,
  PromptsDataResponse,
} from '../../types/prompt-tracking-types';

interface UsePromptTrackingDataParams {
  searchQuery?: string;
  selectedIntent?: PromptIntent | 'all';
  brandName?: string;
  autoFetch?: boolean;
}

interface UsePromptTrackingDataResult {
  data: PromptsDataResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  updateFilters: (filters: Partial<PromptFilters>) => void;
}

export function usePromptTrackingData(
  params: UsePromptTrackingDataParams = {}
): UsePromptTrackingDataResult {
  const {
    searchQuery: initialSearchQuery = '',
    selectedIntent: initialIntent = 'all',
    brandName: initialBrandName,
    autoFetch = true,
  } = params;

  const [data, setData] = useState<PromptsDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [filters, setFilters] = useState<PromptFilters>({
    searchQuery: initialSearchQuery,
    intent: initialIntent,
    brandName: initialBrandName,
  });

  useEffect(() => {
    if (!autoFetch && refetchTrigger === 0) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchPrompts(filters)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err : new Error('Failed to load prompts'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [filters, refetchTrigger, autoFetch]);

  const refetch = useCallback(() => setRefetchTrigger((n) => n + 1), []);

  const updateFilters = useCallback(
    (newFilters: Partial<PromptFilters>) =>
      setFilters((prev) => ({ ...prev, ...newFilters })),
    []
  );

  return { data, isLoading, error, refetch, updateFilters };
}

export function usePromptSummary() {
  const { data, isLoading, error } = usePromptTrackingData({ autoFetch: true });
  return { summary: data?.summary ?? null, isLoading, error };
}